<template>
  <div class="container-fluid">
    <div class="page-title">
      <div class="row">
        <div class="col-6">
          <h3>Consultar viajes</h3>
        </div>
        <div class="col-6 text-end">
          <div class="d-flex justify-content-end gap-2 flex-wrap">
            <report-export-buttons
              title="Reporte de viajes"
              subtitle="Consulta de viajes"
              file-name="reporte-viajes"
              :columns="reportColumns"
              :rows="reportRows"
              :disabled="loading"
            />
            <button class="btn btn-primary" @click="$router.push('/trips/crear')">
              Crear viaje
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">Fecha</label>
            <input v-model="filters.fecha" type="date" class="form-control" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Origen</label>
            <input v-model="filters.origen" type="text" class="form-control" placeholder="Buscar origen" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Destino</label>
            <input v-model="filters.destino" type="text" class="form-control" placeholder="Buscar destino" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Usuario</label>
            <input v-model="filters.usuario" type="text" class="form-control" placeholder="Nombre o id" />
          </div>
          <div class="col-md-3">
            <label class="form-label">Estado</label>
            <select v-model="filters.estado" class="form-select">
              <option value="">Todos</option>
              <option value="ACTIVO">ACTIVO</option>
              <option value="CANCELADO">CANCELADO</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Activo / Cancelado</label>
            <select v-model="filters.activo" class="form-select">
              <option value="">Todos</option>
              <option value="true">Solo activos</option>
              <option value="false">Solo cancelados</option>
            </select>
          </div>
          <!-- <div class="col-md-3">
            <label class="form-label">Por página</label>
            <select v-model.number="pagination.limit" class="form-select">
              <option :value="20">20</option>
            </select>
          </div> -->
          <div class="col-md-3 text-end">
            <button class="btn btn-primary me-2" @click="search">
              Buscar
            </button>
            <button class="btn btn-outline-danger" @click="clearFilters">
              Limpiar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <div v-else-if="errorMsg" class="alert alert-danger" role="alert">
      {{ errorMsg }}
    </div>

    <div v-else class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>Viajero</th>
                <th>Fecha</th>
                <th>Origen</th>
                <th>Destino</th>
                <th class="text-center">Solicitudes</th>
                <th>Estado</th>
                <th class="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="trips.length === 0">
                <td colspan="7" class="text-center text-muted py-4">No hay viajes publicados.</td>
              </tr>
              <tr v-for="trip in trips" :key="trip.id">
                <td>
                  <span class="badge bg-primary">{{ trip.travelerName || '-' }}</span>
                </td>
                <td>
                  <div class="fw-semibold">{{ formatDate(trip.tripDate) }}</div>
                  <div class="text-muted small">{{ formatTime(trip.tripTime) }}</div>
                </td>
                <td class="text-break">{{ trip.originAddress || '-' }}</td>
                <td class="text-break">{{ trip.destinationAddress || '-' }}</td>
                <td class="text-center">
                  <span class="badge" :class="Number(trip.pendingPackages || 0) > 0 ? 'bg-warning text-dark' : 'bg-light text-muted'">
                    {{ Number(trip.pendingPackages || 0) }}
                  </span>
                </td>
                <td>
                  <span class="badge" :class="trip.status === 'CANCELADO' ? 'bg-danger' : 'bg-success'">
                    {{ trip.status || 'ACTIVO' }}
                  </span>
                </td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    @click="$router.push(`/trips/detalle/${trip.id}`)"
                  >
                    Detalles
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.totalPages > 1">
          <span class="small text-muted">
            Mostrando {{ trips.length }} de {{ pagination.total }} viajes
          </span>
          <ul class="pagination m-0 pagination-primary">
            <li class="page-item" :class="{ disabled: pagination.page === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPage(pagination.page - 1)">&laquo;</a>
            </li>
            <li
              v-for="i in pageNumbers"
              :key="i"
              class="page-item"
              :class="{ active: i === pagination.page }"
            >
              <a class="page-link" href="#" @click.prevent="goToPage(i)">{{ i }}</a>
            </li>
            <li class="page-item" :class="{ disabled: pagination.page === pagination.totalPages }">
              <a class="page-link" href="#" @click.prevent="goToPage(pagination.page + 1)">&raquo;</a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { tripsApi } from '@/api/trips';
import ReportExportButtons from '@/components/reports/ReportExportButtons.vue';

export default {
  name: 'ConsultarViajes',
  components: { ReportExportButtons },
  data() {
    return {
      trips: [],
      loading: false,
      errorMsg: '',
      filters: {
        fecha: '',
        origen: '',
        destino: '',
        usuario: '',
        estado: '',
        activo: '',
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
      },
    };
  },
  computed: {
    pageNumbers() {
      const total = this.pagination.totalPages || 1;
      const current = this.pagination.page;
      const start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      const result = [];
      for (let i = start; i <= end; i += 1) result.push(i);
      return result;
    },
    reportColumns() {
      return [
        { key: 'travelerName', label: 'Viajero' },
        { key: 'tripDate', label: 'Fecha viaje' },
        { key: 'tripTime', label: 'Hora viaje' },
        { key: 'originAddress', label: 'Origen' },
        { key: 'destinationAddress', label: 'Destino' },
        { key: 'pendingPackages', label: 'Solicitudes' },
        { key: 'status', label: 'Estado' },
      ];
    },
    reportRows() {
      return this.trips.map((trip) => ({
        travelerName: trip.travelerName || '-',
        tripDate: this.formatDate(trip.tripDate),
        tripTime: this.formatTime(trip.tripTime),
        originAddress: trip.originAddress || '-',
        destinationAddress: trip.destinationAddress || '-',
        pendingPackages: Number(trip.pendingPackages || 0),
        status: trip.status || 'ACTIVO',
      }));
    },
  },
  mounted() {
    this.fetchTrips();
  },
  methods: {
    formatDate(d) {
      if (!d) return '-';
      const date = new Date(d);
      return date.toLocaleDateString('es-ES');
    },
    formatTime(d) {
      if (!d) return '-';
      const date = new Date(d);
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    },
    buildParams() {
      const params = {
        page: this.pagination.page,
        limit: this.pagination.limit,
      };
      if (this.filters.fecha) params.fecha = this.filters.fecha;
      if (this.filters.origen) params.origen = this.filters.origen;
      if (this.filters.destino) params.destino = this.filters.destino;
      if (this.filters.usuario) params.usuario = this.filters.usuario;
      if (this.filters.estado) params.estado = this.filters.estado;
      if (this.filters.activo !== '') params.activo = this.filters.activo;
      return params;
    },
    async fetchTrips() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const { data } = await tripsApi.getAll(this.buildParams());
        this.trips = data?.items || [];
        this.pagination.total = data?.total || 0;
        this.pagination.totalPages = data?.totalPages || 1;
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'Error al cargar viajes.';
      } finally {
        this.loading = false;
      }
    },
    async search() {
      this.pagination.page = 1;
      await this.fetchTrips();
    },
    async goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.pagination.page) return;
      this.pagination.page = page;
      await this.fetchTrips();
    },
    async clearFilters() {
      this.filters = {
        fecha: '',
        origen: '',
        destino: '',
        usuario: '',
        estado: '',
        activo: '',
      };
      this.pagination.page = 1;
      await this.fetchTrips();
    },
  },
};
</script>

